import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  LuActivity,
  LuArrowLeft,
  LuChevronRight,
  LuFeather,
  LuGift,
  LuHeart,
  LuLeaf,
  LuMapPin,
  LuShare2,
  LuShieldOff,
  LuSprout,
  LuTruck,
  LuVegan,
  LuWheatOff,
} from "react-icons/lu";
import AddToCartButton from "../components/AddToCartButton";
import EProteinScore from "../components/EProteinScore";
import HealthBadge from "../components/HealthBadge";
import NutriScore from "../components/NutriScore";
import NutritionSummary from "../components/NutritionSummaryCard";
import { perDayBasis } from "../components/KitPerDay";
import PriceDisplay from "../components/PriceDisplay";
import ProductBenefits from "../components/ProductBenefits";
import ProductImage from "../components/ProductImage";
import QuantitySelector from "../components/QuantitySelector";
import { RecipeList } from "../components/RecipeCard";
import ShopHeader from "../components/ShopHeader";
import { DetailSkeleton, ErrorState } from "../components/States";
import { useCart } from "../hooks/useCart";
import { useShopProduct } from "../hooks/useShopProduct";
import { useFavorites, useToast } from "../hooks/useShopUi";
import { getProductRecipes } from "../../../network/service/ShopService";
import { scaleNutrition } from "../utils/nutritionCalculator";
import { ATTRIBUTE_LABELS, formatAmount, formatPrice } from "../utils/scoreFormatter";
import "../shop.css";

const ATTRIBUTE_ICONS = {
  ORGANIC: LuLeaf,
  GLUTEN_FREE: LuWheatOff,
  LOW_GI: LuActivity,
  UNPOLISHED: LuSprout,
  NO_ADDITIVES: LuShieldOff,
  NATURAL: LuFeather,
  FARM_FRESH: LuLeaf,
  VEGAN: LuVegan,
};

const TABS = ["Overview", "Nutrition", "Benefits", "Recipes"];

export default function ProductDetailsScreen() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [params] = useSearchParams();
  const storeId = Number(params.get("store"));
  const { data: product, status, error, retry } = useShopProduct(storeId, Number(productId));
  useCart(); // loads the cart so the add button knows what is already in it

  if (!storeId) {
    return (
      <div className="ep-shop">
        <ShopHeader title="Product" onBack={() => navigate("/shop")} />
        <ErrorState error={{ status: 404, message: "Open the product from a store's listing." }} notFoundTitle="Choose a store first" />
      </div>
    );
  }
  if (status === "loading") {
    return (
      <div className="ep-shop">
        <DetailSkeleton />
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="ep-shop">
        <ShopHeader title="Product" onBack={() => navigate(-1)} />
        <ErrorState
          error={error}
          onRetry={retry}
          action={
            error?.status === 404 && (
              <button type="button" className="ep-btn is-outline" onClick={() => navigate(-1)}>
                Back to products
              </button>
            )
          }
        />
      </div>
    );
  }
  return <ProductDetails product={product} storeId={storeId} />;
}

function ProductDetails({ product, storeId }) {
  const navigate = useNavigate();
  const { isFavorite, toggle } = useFavorites();
  const { show, toast } = useToast();
  const [packId, setPackId] = useState(product.packs[0]?.priceId ?? null);
  const [imageIndex, setImageIndex] = useState(0);
  const [tab, setTab] = useState("Overview");

  const pack = product.packs.find((p) => p.priceId === packId) ?? null;
  const { nutrition } = product;
  const favorite = isFavorite(product.productId);
  // The API scales each pack (a counted pack of a kit is one whole kit).
  const perPack = useMemo(
    () => pack?.nutrition ?? scaleNutrition(nutrition?.per100g, pack?.grams),
    [nutrition, pack],
  );
  // The nutrition screen opens on the pack chosen here.
  const nutritionPath = `/shop/product/${product.productId}/nutrition?store=${storeId}${packId ? `&pack=${packId}` : ""}`;

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: product.name, text: `${product.name} on EatProtein`, url });
      } else {
        await navigator.clipboard.writeText(url);
        show("Link copied");
      }
    } catch (e) {
      if (e?.name !== "AbortError") show("Could not share this product");
    }
  };

  return (
    <div className="ep-shop">
      <div className="ep-detail-media">
        <div className="ep-detail-top">
          <button type="button" className="ep-icon-btn on-image is-dark" aria-label="Go back" onClick={() => navigate(-1)}>
            <LuArrowLeft />
          </button>
          <div>
            <button
              type="button"
              className={`ep-icon-btn on-image ${favorite ? "is-active" : ""}`}
              aria-label={favorite ? `Remove ${product.name} from favorites` : `Add ${product.name} to favorites`}
              aria-pressed={favorite}
              onClick={() => show(toggle(product.productId) ? "Added to favorites" : "Removed from favorites")}
            >
              <LuHeart fill={favorite ? "currentColor" : "none"} />
            </button>
            <button type="button" className="ep-icon-btn on-image" aria-label="Share product" onClick={share}>
              <LuShare2 />
            </button>
          </div>
        </div>
        <ProductImage path={product.images[imageIndex] ?? product.imagePath} alt={product.name} eager />
        {product.images.length > 1 && (
          <div className="ep-thumbs" role="tablist" aria-label="Product photos">
            {product.images.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === imageIndex}
                aria-label={`Photo ${i + 1}`}
                className={`ep-thumb-dot ${i === imageIndex ? "is-selected" : ""}`}
                onClick={() => setImageIndex(i)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="ep-detail-body">
        <div className="ep-detail-name">
          <h1>
            {product.name}
            {product.localName && (
              <>
                {" "}
                <span className="ep-local" lang="te">
                  ({product.localName})
                </span>
              </>
            )}
          </h1>
          {product.qualityTags.length > 0 && <p className="ep-tagline">{product.qualityTags.join(" • ")}</p>}
        </div>

        {nutrition ? (
          // Score and macros open the full Nutrition Information screen.
          <div
            role="link"
            tabIndex={0}
            className="ep-nutrition-link"
            aria-label={`View nutrition information for ${product.name}`}
            onClick={() => navigate(nutritionPath)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), navigate(nutritionPath))}
          >
            <div className="ep-scoreline">
              <NutriScore grade={nutrition.health.nutriScore?.grade} size="large" />
              <HealthBadge nutrition={nutrition} size="large" />
              <EProteinScore score={nutrition.health.eatProteinScore} size={56} stroke={5} />
            </div>
            {nutrition.perDay ? (
              <NutritionSummary values={nutrition.perDay.perPerson} basis={perDayBasis(nutrition.perDay)} />
            ) : nutrition.kit ? (
              <NutritionSummary values={nutrition.kit.total} basis="per kit" />
            ) : perPack ? (
              <NutritionSummary values={perPack} basis={`per ${pack.label}`} />
            ) : (
              <NutritionSummary values={nutrition.per100g} />
            )}
          </div>
        ) : (
          <p className="ep-unavailable">Nutrition information unavailable for this product.</p>
        )}

        {product.packs.length > 0 ? (
          <div className="ep-buy">
            <QuantitySelector packs={product.packs} selectedId={packId} onSelect={setPackId} />
            <PriceDisplay pack={pack} size="large" />
            <AddToCartButton
              priceId={pack?.priceId}
              productName={product.name}
              packLabel={pack?.label}
              size="large"
              disabled={!product.inStock || !pack}
              onMessage={show}
            />
          </div>
        ) : (
          <p className="ep-unavailable">Not available in this store right now.</p>
        )}
        {perPack && nutrition?.per100g && !nutrition.kit && (
          <p className="ep-pack-note">
            Per 100 g: <b>{formatAmount(nutrition.per100g.protein, "g")} protein</b> ·{" "}
            {formatAmount(nutrition.per100g.calories, "kcal")}
          </p>
        )}

        <DeliveryInfo delivery={product.delivery} />

        <div className="ep-tabs" role="tablist" aria-label="Product information">
          {TABS.map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              id={`tab-${name}`}
              aria-selected={tab === name}
              aria-controls={`panel-${name}`}
              className={`ep-tab ${tab === name ? "is-selected" : ""}`}
              // Nutrition opens its own screen, as in the app design.
              onClick={() => (name === "Nutrition" ? navigate(nutritionPath) : setTab(name))}
            >
              {name}
            </button>
          ))}
        </div>

        <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} className="ep-panel-card">
          {tab === "Overview" && <Overview product={product} />}
          {tab === "Benefits" && (
            <>
              <h2 className="ep-section-title">Product Benefits</h2>
              {nutrition ? <ProductBenefits benefits={nutrition.benefits} /> : <p className="ep-prose">Nutrition information unavailable.</p>}
            </>
          )}
          {tab === "Recipes" && <RecipesTab productId={product.productId} preview={product.recipes} />}
        </div>
      </div>
      {toast}
    </div>
  );
}

function DeliveryInfo({ delivery }) {
  const items = [
    delivery.deliveryTime && { Icon: LuTruck, title: delivery.deliveryTime.replace(/\bmins?\b/i, "min"), text: "Delivery" },
    delivery.distanceKm !== null && { Icon: LuMapPin, title: `${delivery.distanceKm} km`, text: "From Store" },
    delivery.freeDeliveryMin > 0
      ? { Icon: LuGift, title: "Free delivery", text: `on orders above ${formatPrice(delivery.freeDeliveryMin)}` }
      : delivery.deliveryCharge > 0
        ? { Icon: LuGift, title: `${formatPrice(delivery.deliveryCharge)} delivery`, text: delivery.storeName }
        : { Icon: LuGift, title: "Free delivery", text: delivery.storeName },
  ].filter(Boolean);

  return (
    <div className="ep-delivery" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map(({ Icon, title, text }) => (
        <div key={text + title}>
          <Icon aria-hidden="true" />
          <span style={{ minWidth: 0 }}>
            <b>{title}</b>
            {text}
          </span>
        </div>
      ))}
    </div>
  );
}

function Overview({ product }) {
  return (
    <>
      <h2 className="ep-section-title">Product Description</h2>
      <p className="ep-prose">{product.description || "No description has been added for this product yet."}</p>
      {product.attributes.length > 0 && (
        <div className="ep-attrs">
          {product.attributes.map((code) => {
            const Icon = ATTRIBUTE_ICONS[code] ?? LuLeaf;
            return (
              <div key={code} className="ep-attr">
                <span className="ep-attr-icon" aria-hidden="true">
                  <Icon />
                </span>
                {ATTRIBUTE_LABELS[code] ?? code}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function RecipesTab({ productId, preview }) {
  const [all, setAll] = useState(null);
  const [error, setError] = useState("");
  const recipes = all ?? preview.items;

  useEffect(() => setAll(null), [productId]);

  return (
    <>
      <div className="ep-section-title">
        <span>Recipe Ideas</span>
        {!all && preview.total > preview.items.length && (
          <button
            type="button"
            className="ep-link"
            onClick={() =>
              getProductRecipes(productId)
                .then((page) => setAll(page.items))
                .catch((e) => setError(e.message))
            }
          >
            View All <LuChevronRight aria-hidden="true" />
          </button>
        )}
      </div>
      {error && <p className="ep-prose">{error}</p>}
      {preview.total ? <RecipeList recipes={recipes} grid={Boolean(all)} /> : <p className="ep-prose">No recipes for this product yet.</p>}
    </>
  );
}
